import { useState } from 'react';
import type { FormInstance } from 'antd';

import { useSetBeraStepKeyResult } from '@/hooks/useSetBeraStepKeyResult';
import { useUpdateBeraReportV2 } from '@/hooks/useUpdateBeraReportV2';
import { getCheckedStepKeys, toUpdatePayload } from '@/components/views/BeraJob/BeraReportV2/Evaluation/utils';
import {
	EMPTY_JOB_ELEMENT,
	EMPTY_STEP_FIELD,
	EMPTY_STEP_KEY_VALUES,
	EMPTY_TASK_RPN
} from '@/components/views/BeraJob/BeraReportV2/constants';

import type { SetBeraStepKeyResultsParams } from '@/hooks/useSetBeraStepKeyResult';
import type {
	EvaluationStep,
	InformationsValues,
	StepFormValues,
	StepKeyFormValues,
	StepKeyResultValues,
	UpdateBeraReportV2Payload
} from '@/components/views/BeraJob/BeraReportV2/Evaluation/types';

interface UseSaveEvaluationParams {
	organizationId: string;
	companyId: string;
	reportId: string;
	fileId: string;
	form: FormInstance;
}

function toStepKeyResultValues(stepKeyValues?: StepKeyFormValues): StepKeyResultValues {
	if (!stepKeyValues?.step_key) {
		return EMPTY_STEP_KEY_VALUES;
	}

	const result: StepKeyResultValues = {
		stress_level_id: stepKeyValues.stress_level_id ?? null,
		frequency_id: stepKeyValues.frequency_id ?? null,
		total_task_duration_id: stepKeyValues.total_task_duration_id ?? null,
		job_element: stepKeyValues.job_element || EMPTY_JOB_ELEMENT,
		task_rpn: Number(stepKeyValues.task_rpn) || EMPTY_TASK_RPN
	};

	return result;
}

export function useSaveEvaluation({ organizationId, companyId, reportId, fileId, form }: UseSaveEvaluationParams) {
	const [currentStepResultId, setCurrentStepResultId] = useState<string | null>(null);

	const { mutateAsync: updateBeraReport, isLoading: isUpdatingReport } = useUpdateBeraReportV2();
	const { mutateAsync: setKeyResult, isLoading: isSettingKeyResult } = useSetBeraStepKeyResult();

	const isSaving = isUpdatingReport || isSettingKeyResult;

	function getStepKeyValues(stepId: string, stepKeyId: string): StepKeyFormValues | undefined {
		const result: StepKeyFormValues | undefined = form.getFieldValue([fileId, stepId, stepKeyId]);

		return result;
	}

	async function saveInformations(values: InformationsValues): Promise<void> {
		const params = { organizationId, companyId, reportId, values };
		const payload = toUpdatePayload(params);

		await updateBeraReport(payload);
	}

	async function saveStep(step: number): Promise<void> {
		const payload: UpdateBeraReportV2Payload = {
			organization_id: organizationId,
			company_id: companyId,
			id: reportId,
			step
		};

		await updateBeraReport(payload);
	}

	async function saveStepKey(stepId: string, stepKeyId: string, isEmpty: boolean = false): Promise<void> {
		let values: StepKeyResultValues = EMPTY_STEP_KEY_VALUES;

		if (!isEmpty) {
			values = toStepKeyResultValues(getStepKeyValues(stepId, stepKeyId));
		}

		const payload: SetBeraStepKeyResultsParams = {
			...values,
			custom_report_step_key_id: stepKeyId,
			id: reportId,
			organization_id: organizationId,
			company_id: companyId
		};

		const result = await setKeyResult(payload);

		setCurrentStepResultId(result.id);
	}

	async function savePendingStepKeys(step: EvaluationStep): Promise<void> {
		const stepKeys = step.step_key ?? [];
		const [firstStepKey] = stepKeys;
		const isEmptyStepChecked: boolean = !!form.getFieldValue([fileId, step.id, EMPTY_STEP_FIELD]);

		if (isEmptyStepChecked && firstStepKey) {
			await saveStepKey(step.id, firstStepKey.id, true);
			return;
		}

		const values: StepFormValues | undefined = form.getFieldValue([fileId, step.id]);
		const checkedStepKeys = getCheckedStepKeys({ step, values });
		const pendingSaves = checkedStepKeys.map((stepKey) => saveStepKey(step.id, stepKey.id));

		await Promise.all(pendingSaves);
	}

	return {
		isSaving,
		currentStepResultId,
		saveInformations,
		saveStep,
		saveStepKey,
		savePendingStepKeys
	};
}
